import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import DeploymentLogViewer from '../../components/DeploymentLogViewer';
import {
    Box, Typography, Paper, Button, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Alert, CircularProgress,
    IconButton, Tooltip, useTheme, FormControl, FormControlLabel, Checkbox,
    InputLabel, Select, MenuItem,
    Divider, LinearProgress, Collapse, Table, TableHead, TableRow, TableCell, TableBody,
    Tabs, Tab, Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Edit as EditIcon,
    PlayArrow as PlayIcon, RocketLaunch as DeployIcon, CloudUpload as UploadIcon,
    HealthAndSafety as HealthIcon, Stop as StopIcon,
    RestartAlt as RestartIcon, Cable as SshIcon,
    Checklist as PreflightIcon,
    Assessment as LogsIcon, Speed as DeepHealthIcon,
    ExpandMore as ExpandIcon, ExpandLess as CollapseIcon,
    CheckCircle as PassIcon, Warning as WarnIcon, Error as FailIcon,
    Info as InfoIcon,
    MonitorHeart as HealthDetailIcon,
    LocalHospital as DrainIcon,
    ContentCopy as ContentCopyIcon,
    Search as SearchIcon,
    Dns as DiskBudgetIcon,
    Sync as ReplicateIcon,
    Star as DefaultClusterIcon,
    StarBorder as NotDefaultClusterIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';
import ClickHouseBackupsTab from './ClickHouseBackupsTab';
import ConfirmDialog from '../../components/ConfirmDialog';
import ClickHouseHealthDetailDialog from './ClickHouseHealthDetailDialog';
import ClickHouseTenantUsagePanel from './ClickHouseTenantUsagePanel';
import ClickHouseAuditLogDialog from './ClickHouseAuditLogDialog';
import ClickHouseSqlConsoleDialog from './ClickHouseSqlConsoleDialog';
import ClickHouseRetentionPanel from './ClickHouseRetentionPanel';
import ClickHouseHealthBanner from './ClickHouseHealthBanner';
import ClickHouseAlertRecipients from './ClickHouseAlertRecipients';
import ClickHouseCardinalityPanel from './ClickHouseCardinalityPanel';
import ClickHouseTopologyDiagram from './ClickHouseTopologyDiagram';
import ClickHouseConsistencyDialog from './ClickHouseConsistencyDialog';
import ClickHouseTenantMigrationDialog from './ClickHouseTenantMigrationDialog';
import ClickHouseRlsPanel from './ClickHouseRlsPanel';
import { toServerDate } from '../../timezone';

const statusColors = {
    PENDING: 'default', DEPLOYING: 'warning', RUNNING: 'success', STOPPED: 'default', ERROR: 'error',
};

const clusterStatusColors = {
    PENDING: 'default', CONFIGURING: 'warning', RUNNING: 'success', DEGRADED: 'warning', STOPPED: 'default', ERROR: 'error',
};

const logStatusColors = {
    STARTED: 'info', IN_PROGRESS: 'warning', SUCCESS: 'success', FAILED: 'error',
};

const ClickHouseNodesPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [nodes, setNodes] = useState([]);
    const [binaries, setBinaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [actionLoading, setActionLoading] = useState({});

    // Add node dialog
    const [addOpen, setAddOpen] = useState(false);
    const NEW_NODE_DEFAULTS = {
        name: '', host: '', sshPort: 22, sshUsername: 'root', sshAuthType: 'PASSWORD',
        sshPassword: '', sshPrivateKey: '', clickhouseHttpPort: 8123, clickhouseTcpPort: 9000,
        dataDir: '/var/lib/clickhouse',
    };
    const [newNode, setNewNode] = useState(NEW_NODE_DEFAULTS);
    // null = the dialog is in "Add" mode; a node id = "Edit" mode.
    const [editingNodeId, setEditingNodeId] = useState(null);

    // Deploy dialog
    const [deployOpen, setDeployOpen] = useState(false);
    const [deployNodeId, setDeployNodeId] = useState(null);
    const [selectedBinaryId, setSelectedBinaryId] = useState('');

    // Upload
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadVersion, setUploadVersion] = useState('');
    const [uploading, setUploading] = useState(false);

    // Pre-flight dialog
    const [preflightOpen, setPreflightOpen] = useState(false);
    const [preflightResult, setPreflightResult] = useState(null);
    const [preflightLoading, setPreflightLoading] = useState(false);
    const [preflightNodeName, setPreflightNodeName] = useState('');

    // Deployment logs dialog
    const [logsOpen, setLogsOpen] = useState(false);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsNodeName, setLogsNodeName] = useState('');
    const [logsNodeId, setLogsNodeId] = useState(null);

    // Deep health dialog
    const [deepHealthOpen, setDeepHealthOpen] = useState(false);
    const [deepHealthResult, setDeepHealthResult] = useState(null);
    const [deepHealthLoading, setDeepHealthLoading] = useState(false);
    const [deepHealthNodeName, setDeepHealthNodeName] = useState('');

    // Node detail expand
    const [expandedNodeId, setExpandedNodeId] = useState(null);

    // Top-level tab
    const [activeTab, setActiveTab] = useState(0);

    // Shared destructive-action confirmation. One dialog re-used for Stop/Restart/Delete
    // on both nodes and clusters so we don't open a new modal definition per handler.
    // Named `confirmState` (not `confirm`) because ESLint's no-restricted-globals
    // treats `confirm` as the DOM `window.confirm` — shadowing it fails the build.
    const [confirmState, setConfirmState] = useState({
        open: false, title: '', message: '', severity: 'warning',
        confirmPhrase: '', confirmLabel: 'Confirm', onConfirm: null,
    });
    const askConfirm = ({ title, message, severity = 'warning',
                          confirmPhrase = '', confirmLabel = 'Confirm',
                          onConfirm }) => {
        setConfirmState({ open: true, title, message, severity, confirmPhrase, confirmLabel, onConfirm });
    };

    // Cluster health-detail dialog state
    const [healthDetail, setHealthDetail] = useState({ open: false, clusterId: null, clusterName: '' });
    // Operator audit log dialog
    const [auditOpen, setAuditOpen] = useState(false);
    // SQL console dialog
    const [sqlConsole, setSqlConsole] = useState({ open: false, clusterId: null });
    // Consistency report dialog
    const [consistencyOpen, setConsistencyOpen] = useState(false);
    // Export state
    const [exportRunning, setExportRunning] = useState(false);
    // Tenant migration recipe dialog
    const [tenantMigrationOpen, setTenantMigrationOpen] = useState(false);
    const handleStateExport = async () => {
        setExportRunning(true);
        try {
            const res = await billingService.stateExport();
            const json = JSON.stringify(res.data, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const ts = new Date().toISOString().replace(/[:.]/g, '-');
            a.download = `verops-clickhouse-state-${ts}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const digest = res.data?.signature?.digest;
            setMessage({
                type: 'success',
                text: digest
                    ? `State exported — SHA-256 ${digest.slice(0, 16)}…`
                    : 'State exported.',
            });
        } catch (e) {
            setMessage({ type: 'error', text: e?.response?.data?.error || e.message || 'Export failed' });
        } finally {
            setExportRunning(false);
        }
    };

    // Set-disk-budget dialog
    const [budgetDialog, setBudgetDialog] = useState({ open: false, node: null, gb: '' });
    const [budgetSaving, setBudgetSaving] = useState(false);
    const openBudgetDialog = (node) => setBudgetDialog({
        open: true, node,
        gb: node.diskBudgetGb != null ? String(node.diskBudgetGb) : '',
    });
    const saveBudget = async (clear = false) => {
        if (!budgetDialog.node) return;
        const gb = clear ? null : Number(budgetDialog.gb);
        if (!clear && (!Number.isFinite(gb) || gb <= 0)) {
            setMessage({ type: 'error', text: 'Enter a positive number of GB or click "Clear budget".' });
            return;
        }
        setBudgetSaving(true);
        try {
            await billingService.setNodeDiskBudget(budgetDialog.node.id, gb);
            setMessage({
                type: 'success',
                text: clear
                    ? `Disk budget cleared on ${budgetDialog.node.name}`
                    : `Disk budget ${gb} GB applied on ${budgetDialog.node.name} — ClickHouse config reloaded`,
            });
            setBudgetDialog({ open: false, node: null, gb: '' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Set disk budget failed' });
        } finally {
            setBudgetSaving(false);
        }
    };

    // Nodes-table filter bar
    const [nodeFilter, setNodeFilter] = useState({ search: '', status: 'all' });
    const filteredNodes = React.useMemo(() => {
        const q = nodeFilter.search.trim().toLowerCase();
        return nodes.filter(n => {
            if (nodeFilter.status !== 'all' && n.status !== nodeFilter.status) return false;
            if (!q) return true;
            return (
                (n.name && n.name.toLowerCase().includes(q)) ||
                (n.host && n.host.toLowerCase().includes(q)) ||
                (n.version && n.version.toLowerCase().includes(q))
            );
        });
    }, [nodes, nodeFilter]);

    // Clusters state
    const [clusters, setClusters] = useState([]);
    const [clustersLoading, setClustersLoading] = useState(false);
    const [createClusterOpen, setCreateClusterOpen] = useState(false);
    const [newCluster, setNewCluster] = useState({ name: '', clusterType: 'CLICKHOUSE', topology: 'single_shard_replicated', shardCount: 1, replicasPerShard: 2 });
    const [assignNodeOpen, setAssignNodeOpen] = useState(false);
    const [assignClusterId, setAssignClusterId] = useState(null);
    const [unassignedNodes, setUnassignedNodes] = useState([]);
    const [assignForm, setAssignForm] = useState({ nodeId: '', shardNum: 1, replicaName: '', nodeRole: 'DATA' });
    const [clusterHealthOpen, setClusterHealthOpen] = useState(false);
    const [clusterHealthResult, setClusterHealthResult] = useState(null);
    const [clusterHealthLoading, setClusterHealthLoading] = useState(false);
    const [clusterHealthName, setClusterHealthName] = useState('');
    const [clusterNodesOpen, setClusterNodesOpen] = useState(false);
    const [clusterNodes, setClusterNodes] = useState([]);
    const [clusterNodesName, setClusterNodesName] = useState('');
    const [clusterNodesLoading, setClusterNodesLoading] = useState(false);
    const [clusterNodesCluster, setClusterNodesCluster] = useState(null);
    const [clusterDeployOpen, setClusterDeployOpen] = useState(false);
    const [clusterDeployId, setClusterDeployId] = useState(null);
    const [clusterDeployBinaryId, setClusterDeployBinaryId] = useState('');
    const [clusterActionLoading, setClusterActionLoading] = useState({});

    // Tenant routing state
    const [assignments, setAssignments] = useState([]);
    const [unassignedTenants, setUnassignedTenants] = useState([]);
    const [availableClusters, setAvailableClusters] = useState([]);
    const [routingLoading, setRoutingLoading] = useState(false);
    const [tenantAssignDialogOpen, setTenantAssignDialogOpen] = useState(false);
    const [tenantAssignOrgId, setTenantAssignOrgId] = useState('');
    const [tenantAssignClusterId, setTenantAssignClusterId] = useState('');
    const [connDialogOpen, setConnDialogOpen] = useState(false);
    const [connClusterId, setConnClusterId] = useState(null);
    const [connClusterName, setConnClusterName] = useState('');
    const [connForm, setConnForm] = useState({ selectedNodeHost: '', dbUsername: 'default', dbPassword: '', databaseName: 'verops' });

    // Delete confirmation dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteNodeId, setDeleteNodeId] = useState(null);
    const [deleteNodeName, setDeleteNodeName] = useState('');
    const [deleteCleanRemote, setDeleteCleanRemote] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Auto-refresh ref
    const autoRefreshRef = useRef(null);

    const loadData = useCallback(async () => {
        try {
            const [nodesRes, binariesRes] = await Promise.all([
                billingService.listClickHouseNodes(),
                billingService.listClickHouseBinaries(),
            ]);
            setNodes(nodesRes.data || []);
            setBinaries(binariesRes.data || []);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load data' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    // Auto-refresh while any node is DEPLOYING — keep polling a few extra cycles
    // after DEPLOYING clears to catch the final ERROR/RUNNING transition
    const deployingCountRef = useRef(0);
    useEffect(() => {
        const hasDeploying = nodes.some(n => n.status === 'DEPLOYING');

        if (hasDeploying) {
            deployingCountRef.current = 0; // reset cooldown
        } else if (deployingCountRef.current < 3 && autoRefreshRef.current) {
            // Keep polling for 3 more cycles (15s) after DEPLOYING disappears
            // to ensure we catch the final status transition
            deployingCountRef.current += 1;
        }

        const shouldPoll = hasDeploying || deployingCountRef.current < 3;

        if (shouldPoll && !autoRefreshRef.current) {
            autoRefreshRef.current = setInterval(loadData, 5000);
        } else if (!shouldPoll && autoRefreshRef.current) {
            clearInterval(autoRefreshRef.current);
            autoRefreshRef.current = null;
        }

        // Clear any stale action loading states when a node transitions out of DEPLOYING
        if (!hasDeploying) {
            setActionLoading({});
        }

        return () => {
            if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
        };
    }, [nodes, loadData]);

    const setNodeAction = (id, loading) => setActionLoading(prev => ({ ...prev, [id]: loading }));

    const handleAddNode = async () => {
        if (!newNode.name || !newNode.host) {
            setMessage({ type: 'error', text: 'Name and host are required' });
            return;
        }
        const validatePort = (port, label) => {
            if (!port || port < 1 || port > 65535) return `${label} must be between 1 and 65535`;
            return null;
        };
        const portErrors = [
            validatePort(newNode.sshPort, 'SSH Port'),
            validatePort(newNode.clickhouseHttpPort, 'HTTP Port'),
            validatePort(newNode.clickhouseTcpPort, 'TCP Port'),
        ].filter(Boolean);
        if (portErrors.length > 0) {
            setMessage({ type: 'error', text: portErrors.join('. ') });
            return;
        }
        if (newNode.clickhouseHttpPort === newNode.clickhouseTcpPort) {
            setMessage({ type: 'error', text: 'HTTP Port and TCP Port must be different' });
            return;
        }
        try {
            if (editingNodeId) {
                // Secret fields left at the masked placeholder are preserved by
                // the backend (updateNode ignores "********"); a real value replaces.
                await billingService.updateClickHouseNode(editingNodeId, newNode);
                setMessage({ type: 'success', text: `Node "${newNode.name}" updated` });
            } else {
                await billingService.createClickHouseNode(newNode);
                setMessage({ type: 'success', text: `Node "${newNode.name}" added` });
            }
            setAddOpen(false);
            setEditingNodeId(null);
            setNewNode(NEW_NODE_DEFAULTS);
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || (editingNodeId ? 'Failed to update node' : 'Failed to add node') });
        }
    };

    // Open the shared node dialog in Edit mode. Secrets aren't returned by the
    // API, so we pre-fill them with the masked placeholder the backend treats as
    // "keep existing" — the operator replaces it only to rotate the credential.
    // This is what lets an operator switch an existing node from password to KEY
    // auth (and paste the private key) without deleting and re-adding it.
    const handleEditNode = (row) => {
        setNewNode({
            name: row.name || '', host: row.host || '',
            sshPort: row.sshPort || 22, sshUsername: row.sshUsername || 'root',
            sshAuthType: row.sshAuthType || 'PASSWORD',
            sshPassword: '********', sshPrivateKey: '********',
            clickhouseHttpPort: row.clickhouseHttpPort || 8123,
            clickhouseTcpPort: row.clickhouseTcpPort || 9000,
            dataDir: row.dataDir || '/var/lib/clickhouse',
        });
        setEditingNodeId(row.id);
        setAddOpen(true);
    };

    const handleOpenAddNode = () => {
        setEditingNodeId(null);
        setNewNode(NEW_NODE_DEFAULTS);
        setAddOpen(true);
    };

    const handleCloseNodeDialog = () => {
        setAddOpen(false);
        setEditingNodeId(null);
    };

    const handleDeleteNode = (id, name) => {
        setDeleteNodeId(id);
        setDeleteNodeName(name || '');
        setDeleteCleanRemote(false);
        setDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        setDeleteLoading(true);
        try {
            const res = await billingService.deleteClickHouseNode(deleteNodeId, deleteCleanRemote);
            const data = res.data;
            let msg = 'Node deleted';
            if (data.logsDeleted > 0) msg += ` (${data.logsDeleted} deployment logs removed)`;
            if (deleteCleanRemote) {
                msg += data.remoteCleanup === 'success'
                    ? ' — remote server cleaned'
                    : ` — remote cleanup: ${data.remoteCleanup || 'skipped'}`;
            }
            setMessage({ type: 'success', text: msg });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete node' });
        } finally {
            setDeleteLoading(false);
            setDeleteOpen(false);
        }
    };

    const handleHealthCheck = async (id) => {
        setNodeAction(id, true);
        try {
            const res = await billingService.healthCheckClickHouseNode(id);
            if (res.data?.success) {
                setMessage({ type: 'success', text: `Node healthy - ${res.data.responseTimeMs}ms (v${res.data.version})` });
            } else {
                setMessage({ type: 'error', text: `Health check failed: ${res.data?.message}` });
            }
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: 'Health check failed' });
        } finally {
            setNodeAction(id, false);
        }
    };

    const handleStartNode = async (id) => {
        setNodeAction(id, true);
        try {
            await billingService.startClickHouseNode(id);
            setMessage({ type: 'success', text: 'Start initiated' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Start failed' });
        } finally {
            setNodeAction(id, false);
        }
    };

    const handleStopNode = async (id) => {
        setNodeAction(id, true);
        try {
            await billingService.stopClickHouseNode(id);
            setMessage({ type: 'success', text: 'ClickHouse stopped' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Stop failed' });
        } finally {
            setNodeAction(id, false);
        }
    };

    const handleRestartNode = async (id) => {
        setNodeAction(id, true);
        try {
            await billingService.restartClickHouseNode(id);
            setMessage({ type: 'success', text: 'Restart initiated' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Restart failed' });
        } finally {
            setNodeAction(id, false);
        }
    };

    const handleTestSsh = async (id) => {
        setNodeAction(id, true);
        try {
            const res = await billingService.testSshClickHouseNode(id);
            if (res.data?.success) {
                setMessage({ type: 'success', text: `SSH OK (${res.data.latencyMs}ms) - ${res.data.systemInfo || ''}` });
            } else {
                setMessage({ type: 'error', text: res.data?.message || 'SSH connection failed' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'SSH test failed' });
        } finally {
            setNodeAction(id, false);
        }
    };

    const handlePreflight = async (id, name) => {
        setPreflightNodeName(name);
        setPreflightResult(null);
        setPreflightOpen(true);
        setPreflightLoading(true);
        try {
            const res = await billingService.preflightClickHouseNode(id);
            setPreflightResult(res.data);
            loadData();
        } catch (err) {
            setPreflightResult({ error: err.response?.data?.error || 'Pre-flight check failed', checks: [], passed: false });
        } finally {
            setPreflightLoading(false);
        }
    };

    const handleViewLogs = (id, name) => {
        setLogsNodeName(name || 'All Nodes');
        setLogsNodeId(id);
        setLogsOpen(true);
    };

    const handleDeepHealth = async (id, name) => {
        setDeepHealthNodeName(name);
        setDeepHealthResult(null);
        setDeepHealthOpen(true);
        setDeepHealthLoading(true);
        try {
            const res = await billingService.deepHealthCheckClickHouseNode(id);
            setDeepHealthResult(res.data);
        } catch (err) {
            setDeepHealthResult({ error: err.response?.data?.error || 'Deep health check failed' });
        } finally {
            setDeepHealthLoading(false);
        }
    };

    const [deployInFlight, setDeployInFlight] = useState(false);
    const handleDeploy = async () => {
        if (!selectedBinaryId) { setMessage({ type: 'error', text: 'Select a binary' }); return; }
        if (deployInFlight) return; // prevent double-click
        setDeployInFlight(true);
        try {
            await billingService.deployClickHouseNode(deployNodeId, selectedBinaryId);
            setMessage({ type: 'success', text: 'Deployment started - status will auto-refresh' });
            setDeployOpen(false);
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Deployment failed' });
        } finally {
            setDeployInFlight(false);
        }
    };

    const handleUploadBinary = async () => {
        if (!uploadFile) { setMessage({ type: 'error', text: 'Select a file' }); return; }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);
            if (uploadVersion) formData.append('version', uploadVersion);
            await billingService.uploadClickHouseBinary(formData);
            setMessage({ type: 'success', text: `Binary "${uploadFile.name}" uploaded` });
            setUploadFile(null);
            setUploadVersion('');
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Upload failed' });
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteBinary = async (id) => {
        try {
            await billingService.deleteClickHouseBinary(id);
            setMessage({ type: 'success', text: 'Binary deleted' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete binary' });
        }
    };

    // ==================== Cluster Handlers ====================

    const loadClusters = useCallback(async () => {
        setClustersLoading(true);
        try {
            const res = await billingService.listClickHouseClusters();
            setClusters(res.data || []);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load clusters' });
        } finally {
            setClustersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 1 || activeTab === 2 || activeTab === 3) loadClusters();
    }, [activeTab, loadClusters]);

    // Auto-refresh the cluster list while any cluster is mid-operation
    // (CONFIGURING). Stops as soon as every cluster is in a terminal state
    // so we don't hammer the backend with polls when nothing's happening.
    useEffect(() => {
        if (activeTab !== 1 && activeTab !== 2 && activeTab !== 3) return undefined;
        const anyConfiguring = clusters.some(c => c.status === 'CONFIGURING');
        if (!anyConfiguring) return undefined;
        const t = setInterval(loadClusters, 5000);
        return () => clearInterval(t);
    }, [clusters, activeTab, loadClusters]);

    const handleCreateCluster = async () => {
        if (!newCluster.name) { setMessage({ type: 'error', text: 'Cluster name is required' }); return; }
        try {
            await billingService.createClickHouseCluster(newCluster);
            setMessage({ type: 'success', text: `Cluster "${newCluster.name}" created` });
            setCreateClusterOpen(false);
            setNewCluster({ name: '', clusterType: 'CLICKHOUSE', topology: 'single_shard_replicated', shardCount: 1, replicasPerShard: 2 });
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create cluster' });
        }
    };

    // The ConfirmDialog (askConfirm) guards the destructive call — this
    // handler assumes confirmation already happened at the call site.
    const handleDeleteCluster = async (id) => {
        try {
            await billingService.deleteClickHouseCluster(id);
            setMessage({ type: 'success', text: 'Cluster deleted' });
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete cluster' });
        }
    };

    const handleOpenAssignNode = async (clusterId) => {
        setAssignClusterId(clusterId);
        setAssignForm({ nodeId: '', shardNum: 1, replicaName: '', nodeRole: 'DATA' });
        setAssignNodeOpen(true);
        try {
            const res = await billingService.getUnassignedNodes();
            setUnassignedNodes(res.data || []);
        } catch {
            setUnassignedNodes([]);
        }
    };

    const handleAssignNode = async () => {
        if (!assignForm.nodeId) { setMessage({ type: 'error', text: 'Select a node' }); return; }
        try {
            await billingService.assignNodeToCluster(assignClusterId, {
                nodeId: assignForm.nodeId,
                shardNum: assignForm.shardNum || null,
                replicaName: assignForm.replicaName || null,
                nodeRole: assignForm.nodeRole,
            });
            setMessage({ type: 'success', text: 'Node assigned to cluster' });
            setAssignNodeOpen(false);
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to assign node' });
        }
    };

    const setClusterAction = (id, loading) => setClusterActionLoading(prev => ({ ...prev, [id]: loading }));

    const handleConfigureCluster = async (id) => {
        setClusterAction(id, true);
        try {
            await billingService.configureCluster(id);
            setMessage({ type: 'success', text: 'Cluster configuration started' });
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Configure failed' });
        } finally {
            setClusterAction(id, false);
        }
    };

    // Promote every MergeTree-family table in the cluster's database to
    // Replicated*MergeTree. Async — per-table progress shows up in the
    // cluster's statusMessage; per-table outcome in the deployment log
    // under MIGRATE_TABLE_OK / MIGRATE_TABLE_FAIL.
    const handleMigrateToReplicated = async (id) => {
        setClusterAction(id, true);
        try {
            await billingService.migrateClusterToReplicated(id);
            setMessage({ type: 'success',
                text: 'Replication migration started — watch the cluster status + deployment log for per-table progress' });
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error',
                text: err.response?.data?.error || 'Migration failed to start' });
        } finally {
            setClusterAction(id, false);
        }
    };

    const handleSetDefaultCluster = async (id, name) => {
        setClusterAction(id, true);
        try {
            await billingService.setDefaultCluster(id);
            setMessage({ type: 'success',
                text: `${name} is now the default landing zone for new organizations` });
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error',
                text: err.response?.data?.error || 'Failed to set default cluster' });
        } finally {
            setClusterAction(id, false);
        }
    };

    const handleDeployCluster = async () => {
        if (!clusterDeployBinaryId) { setMessage({ type: 'error', text: 'Select a binary' }); return; }
        try {
            // Validate topology before deploying
            const validation = await billingService.validateClusterTopology(clusterDeployId);
            const v = validation.data;
            if (v && !v.valid && v.errors?.length > 0) {
                setMessage({ type: 'error', text: 'Topology issues: ' + v.errors.join('; ') });
                return;
            }
            await billingService.deployCluster(clusterDeployId, clusterDeployBinaryId);
            setMessage({ type: 'success', text: 'Cluster deployment started' });
            setClusterDeployOpen(false);
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Cluster deploy failed' });
        }
    };

    const handleStartCluster = async (id) => {
        setClusterAction(id, true);
        try {
            await billingService.startCluster(id);
            setMessage({ type: 'success', text: 'Cluster start initiated' });
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Start cluster failed' });
        } finally {
            setClusterAction(id, false);
        }
    };

    // Graceful stop: drain merges first, then stop. If drain timed out we
    // re-prompt with force=true so the operator can explicitly accept a
    // hard stop.
    const handleGracefulStopCluster = async (id, clusterName, force = false) => {
        setClusterAction(id, true);
        try {
            const res = await billingService.gracefulStopCluster(id, force);
            const r = res.data || {};
            if (r.stopped) {
                setMessage({ type: 'success', text: `Cluster ${clusterName} drained and stopped in ${r.elapsedSeconds}s.` });
            } else {
                // Drain didn't complete cleanly — offer force path.
                askConfirm({
                    title: `Drain timed out on ${clusterName}`,
                    severity: 'error',
                    message: r.stopMessage || 'Drain did not complete within the timeout on at least one DATA node. Forcing a stop now will kill in-flight merges. You can instead cancel, investigate system.merges via Health Detail, and retry later.',
                    confirmLabel: 'Force stop anyway',
                    confirmPhrase: clusterName,
                    onConfirm: () => handleGracefulStopCluster(id, clusterName, true),
                });
            }
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Graceful stop failed' });
        } finally {
            setClusterAction(id, false);
        }
    };

    const handleStopCluster = async (id) => {
        setClusterAction(id, true);
        try {
            await billingService.stopCluster(id);
            setMessage({ type: 'success', text: 'Cluster stopped' });
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Stop cluster failed' });
        } finally {
            setClusterAction(id, false);
        }
    };

    const handleClusterHealth = async (id, name) => {
        setClusterHealthName(name);
        setClusterHealthResult(null);
        setClusterHealthOpen(true);
        setClusterHealthLoading(true);
        try {
            const res = await billingService.clusterHealthCheck(id);
            setClusterHealthResult(res.data);
            loadClusters();
        } catch (err) {
            setClusterHealthResult({ error: err.response?.data?.error || 'Health check failed' });
        } finally {
            setClusterHealthLoading(false);
        }
    };

    const handleViewClusterNodes = async (id, name) => {
        setClusterNodesName(name);
        setClusterNodes([]);
        setClusterNodesOpen(true);
        setClusterNodesLoading(true);
        // Snapshot the cluster object so the topology diagram can read
        // replicasPerShard / shardCount without refetching.
        setClusterNodesCluster(clusters.find(c => c.id === id) || null);
        try {
            const res = await billingService.getClusterNodes(id);
            setClusterNodes(res.data || []);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load cluster nodes' });
        } finally {
            setClusterNodesLoading(false);
        }
    };

    const handleRemoveNodeFromCluster = async (clusterId, nodeId) => {
        try {
            await billingService.removeNodeFromCluster(clusterId, nodeId);
            setMessage({ type: 'success', text: 'Node removed from cluster' });
            const res = await billingService.getClusterNodes(clusterId);
            setClusterNodes(res.data || []);
            loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to remove node' });
        }
    };

    // ==================== Tenant Routing Handlers ====================

    const loadRoutingData = useCallback(async () => {
        setRoutingLoading(true);
        try {
            const [assignRes, unassignedRes, clustersRes] = await Promise.all([
                billingService.listTenantClusterAssignments(),
                billingService.getUnassignedTenants(),
                billingService.getAvailableClustersForTenants(),
            ]);
            setAssignments(assignRes.data || []);
            setUnassignedTenants(unassignedRes.data || []);
            setAvailableClusters(clustersRes.data || []);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load tenant routing data' });
        } finally {
            setRoutingLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 2) loadRoutingData();
    }, [activeTab, loadRoutingData]);

    const handleAssignTenant = async () => {
        if (!tenantAssignOrgId || !tenantAssignClusterId) {
            setMessage({ type: 'error', text: 'Select both a tenant and a cluster' });
            return;
        }
        try {
            await billingService.assignTenantToCluster(tenantAssignOrgId, tenantAssignClusterId);
            setMessage({ type: 'success', text: 'Tenant assigned to cluster' });
            setTenantAssignDialogOpen(false);
            setTenantAssignOrgId('');
            setTenantAssignClusterId('');
            loadRoutingData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to assign tenant' });
        }
    };

    const handleUnassignTenant = async (orgId, orgName) => {
        if (!window.confirm(`Remove "${orgName}" from its cluster? Their datasource will become unavailable until reassigned.`)) return;
        try {
            await billingService.unassignTenant(orgId);
            setMessage({ type: 'success', text: `"${orgName}" unassigned from cluster` });
            loadRoutingData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to unassign tenant' });
        }
    };

    const handleOpenConnDialog = (cluster) => {
        setConnClusterId(cluster.id);
        setConnClusterName(cluster.name);
        // Parse existing jdbcUrl to extract host:port for the dropdown
        let selectedNodeHost = '';
        if (cluster.jdbcUrl) {
            const match = cluster.jdbcUrl.match(/jdbc:(?:ch|clickhouse):\/\/([^/]+)/);
            if (match) selectedNodeHost = match[1];
        }
        setConnForm({
            selectedNodeHost,
            dbUsername: cluster.dbUsername || 'default',
            dbPassword: '',
            databaseName: cluster.databaseName || 'verops',
        });
        setConnDialogOpen(true);
    };

    const handleSaveConnection = async () => {
        if (!connForm.selectedNodeHost) {
            setMessage({ type: 'error', text: 'Select a connection node' });
            return;
        }
        const jdbcUrl = `jdbc:clickhouse://${connForm.selectedNodeHost}/${connForm.databaseName || 'verops'}`;
        try {
            await billingService.updateClusterConnection(connClusterId, { ...connForm, jdbcUrl });
            setMessage({ type: 'success', text: `Connection updated for "${connClusterName}"` });
            setConnDialogOpen(false);
            loadRoutingData();
            if (activeTab === 1) loadClusters();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update connection' });
        }
    };

    const handleTestConnection = async (clusterId) => {
        try {
            const res = await billingService.testClusterConnection(clusterId);
            if (res.data?.success) {
                setMessage({ type: 'success', text: `Connection OK — ClickHouse v${res.data.version}` });
            } else {
                setMessage({ type: 'error', text: `Connection failed: ${res.data?.error}` });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Connection test failed' });
        }
    };

    const formatSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
        if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
        return `${(bytes / 1024).toFixed(0)} KB`;
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        if (ms < 1000) return `${ms}ms`;
        const sec = Math.floor(ms / 1000);
        if (sec < 60) return `${sec}s`;
        return `${Math.floor(sec / 60)}m ${sec % 60}s`;
    };

    const PreflightStatusIcon = ({ status }) => {
        if (status === 'pass') return <PassIcon sx={{ fontSize: 16, color: 'success.main' }} />;
        if (status === 'warn') return <WarnIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
        return <FailIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    };

    const copyToClipboard = (text, label) => {
        if (!text) return;
        try {
            navigator.clipboard.writeText(text);
            setMessage({ type: 'success', text: `${label || 'Copied'}: ${text.length > 60 ? text.substring(0, 60) + '…' : text}` });
        } catch {
            setMessage({ type: 'error', text: 'Copy to clipboard failed — browser permission?' });
        }
    };

    // Disk-low heuristic: thresholds apply against the **budget** if one
    // is configured (since ClickHouse will refuse INSERTs at the budget
    // line, not at disk full), else against total disk.
    //   <15% free OR <20 GB free  = warning
    //   <5%  free OR <10 GB free  = critical
    const diskStatus = ({ diskFreeGb, diskTotalGb, diskBudgetGb }) => {
        if (diskFreeGb == null || diskTotalGb == null || diskTotalGb === 0) return null;
        const ceiling = diskBudgetGb && diskBudgetGb < diskTotalGb ? diskBudgetGb : diskTotalGb;
        // Space consumed relative to the ceiling — derive usedGb then remaining-against-budget.
        const usedGb = diskTotalGb - diskFreeGb;
        const budgetFreeGb = Math.max(0, ceiling - usedGb);
        const pct = ceiling > 0 ? budgetFreeGb / ceiling : 0;
        if (pct < 0.05 || budgetFreeGb < 10) return 'critical';
        if (pct < 0.15 || budgetFreeGb < 20) return 'warning';
        return null;
    };

    const nodeColumns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 120 },
        {
            field: 'host', headerName: 'Host', flex: 1, minWidth: 160,
            renderCell: (p) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box>
                        <Typography variant="body2">{p.value}</Typography>
                        {p.row.osInfo && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                {p.row.osInfo.substring(0, 40)}
                            </Typography>
                        )}
                    </Box>
                    <Tooltip title="Copy host">
                        <IconButton size="small"
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(p.value, 'Host'); }}>
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
        {
            field: 'resources', headerName: 'Resources', width: 200,
            renderCell: (p) => {
                const { cpuCores, ramGb, diskFreeGb, diskTotalGb, diskBudgetGb } = p.row;
                if (!cpuCores && !ramGb) return <Typography variant="caption" color="text.disabled">Not scanned</Typography>;
                const disk = diskStatus(p.row);
                const usedGb = diskTotalGb != null && diskFreeGb != null ? diskTotalGb - diskFreeGb : null;
                return (
                    <Box sx={{ lineHeight: 1.2 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            {cpuCores || '?'} CPU / {ramGb || '?'} GB RAM
                        </Typography>
                        {diskTotalGb != null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem',
                                    color: disk === 'critical' ? 'error.main' : disk === 'warning' ? 'warning.main' : 'text.secondary' }}>
                                    {diskBudgetGb
                                        ? `${usedGb ?? '?'}/${diskBudgetGb}GB budget (disk ${diskTotalGb}GB)`
                                        : `${diskFreeGb}GB free / ${diskTotalGb}GB`}
                                </Typography>
                                {disk && (
                                    <Tooltip title={disk === 'critical'
                                        ? (diskBudgetGb
                                            ? `Critical: within 10 GB of the ${diskBudgetGb} GB budget. ClickHouse will refuse INSERTs imminently.`
                                            : 'Critical: <5% or <10 GB free. Merges may stall. Extend the volume or scale out.')
                                        : (diskBudgetGb
                                            ? `Warning: within 20 GB of the ${diskBudgetGb} GB budget. Plan growth.`
                                            : 'Warning: <15% or <20 GB free. Plan disk growth before ClickHouse starts rejecting INSERTs.')}>
                                        <Chip size="small" color={disk === 'critical' ? 'error' : 'warning'}
                                              label={disk === 'critical' ? 'LOW' : 'low'}
                                              sx={{ fontSize: 9, height: 16 }} />
                                    </Tooltip>
                                )}
                                {diskBudgetGb && (
                                    <Tooltip title={`ClickHouse keep_free_space_bytes enforces a ${diskBudgetGb} GB INSERT ceiling. Click the Dns icon in Actions to change or clear.`}>
                                        <Chip size="small" variant="outlined" color="info"
                                              label="budget" sx={{ fontSize: 9, height: 16 }} />
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            },
        },
        { field: 'version', headerName: 'Version', width: 100, renderCell: (p) => p.value || '-' },
        {
            field: 'status', headerName: 'Status', width: 200,
            renderCell: (p) => {
                const isDeploying = p.value === 'DEPLOYING';
                const isError = p.value === 'ERROR';
                const msg = p.row.statusMessage || '';
                const pctMatch = msg.match(/(\d+)%/);
                const pct = pctMatch ? parseInt(pctMatch[1], 10) : null;
                // Extract step info from messages like "Failed at step 5/9: ..."
                const stepMatch = msg.match(/step (\d+)\/(\d+)/i) || msg.match(/\[(\d+)\/(\d+)\]/);
                return (
                    <Tooltip title={msg || p.value} placement="top" arrow>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%', py: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip label={p.value} size="small" color={statusColors[p.value] || 'default'} />
                                {isDeploying && <CircularProgress size={14} />}
                                {p.row.consecutiveFailures > 2 && (
                                    <Tooltip title={`${p.row.consecutiveFailures} consecutive failures`}>
                                        <WarnIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                    </Tooltip>
                                )}
                            </Box>
                            {(isDeploying || isError) && msg && (
                                <Box sx={{ width: '100%', mt: 0.3 }}>
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.65rem',
                                        color: isError ? 'error.main' : 'text.secondary',
                                        lineHeight: 1.2, display: 'block'
                                    }}>
                                        {msg.length > 50 ? msg.substring(0, 50) + '...' : msg}
                                    </Typography>
                                    {isDeploying && pct !== null && (
                                        <LinearProgress variant="determinate" value={pct} sx={{ height: 3, borderRadius: 1, mt: 0.3 }} />
                                    )}
                                    {isDeploying && pct === null && stepMatch && (
                                        <LinearProgress variant="determinate"
                                            value={Math.round((parseInt(stepMatch[1]) / parseInt(stepMatch[2])) * 100)}
                                            sx={{ height: 3, borderRadius: 1, mt: 0.3 }} />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'lastHealthCheck', headerName: 'Last Check', width: 130,
            renderCell: (p) => toServerDate(p.value)?.toLocaleString() ?? 'Never',
        },
        {
            field: 'actions', headerName: 'Actions', width: 350, sortable: false,
            renderHeader: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <span style={{ fontWeight: 600 }}>Actions</span>
                    <HelpIcon docKey="node_actions_legend" />
                </Box>
            ),
            renderCell: (p) => {
                const isLoading = actionLoading[p.row.id];
                return (
                    <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                        {isLoading && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                        <Tooltip title="Pre-flight Check">
                            <IconButton size="small" onClick={() => handlePreflight(p.row.id, p.row.name)} disabled={isLoading}>
                                <PreflightIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit node (connection / SSH auth)">
                            <IconButton size="small" onClick={() => handleEditNode(p.row)} disabled={isLoading}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Test SSH">
                            <IconButton size="small" onClick={() => handleTestSsh(p.row.id)} disabled={isLoading}>
                                <SshIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Health Check">
                            <IconButton size="small" onClick={() => handleHealthCheck(p.row.id)} disabled={isLoading}>
                                <HealthIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Deep Health">
                            <IconButton size="small" onClick={() => handleDeepHealth(p.row.id, p.row.name)} disabled={isLoading}>
                                <DeepHealthIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={p.row.diskBudgetGb
                            ? `Disk budget: ${p.row.diskBudgetGb} GB (click to change / clear)`
                            : 'Set disk budget (keep_free_space_bytes)'}>
                            <IconButton size="small" onClick={() => openBudgetDialog(p.row)} disabled={isLoading}
                                        sx={{ color: p.row.diskBudgetGb ? 'info.main' : undefined }}>
                                <DiskBudgetIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        {!['RUNNING', 'STOPPED'].includes(p.row.status) && (
                            <Tooltip title={p.row.status === 'DEPLOYING' ? 'Deployment in progress' : 'Deploy'}>
                                <span>
                                <IconButton size="small" onClick={() => { setDeployNodeId(p.row.id); setDeployOpen(true); }} disabled={isLoading || p.row.status === 'DEPLOYING'}>
                                    <DeployIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Start">
                            <IconButton size="small" onClick={() => handleStartNode(p.row.id)} disabled={isLoading} sx={{ color: 'success.main' }}>
                                <PlayIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Stop">
                            <IconButton size="small" onClick={() => askConfirm({
                                title: `Stop node ${p.row.name}?`,
                                severity: 'warning',
                                message: 'Stops the ClickHouse process on this node. In-flight writes may be lost — not a graceful drain. If this node serves tenant traffic, use the cluster-level Graceful Stop instead.',
                                confirmLabel: 'Stop',
                                confirmPhrase: p.row.name,
                                onConfirm: () => handleStopNode(p.row.id),
                            })} disabled={isLoading} sx={{ color: 'warning.main' }}>
                                <StopIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Restart">
                            <IconButton size="small" onClick={() => askConfirm({
                                title: `Restart node ${p.row.name}?`,
                                severity: 'warning',
                                message: 'Stops then starts the ClickHouse process. Same write-loss caveat as Stop.',
                                confirmLabel: 'Restart',
                                confirmPhrase: p.row.name,
                                onConfirm: () => handleRestartNode(p.row.id),
                            })} disabled={isLoading}>
                                <RestartIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Logs">
                            <IconButton size="small" onClick={() => handleViewLogs(p.row.id, p.row.name)}>
                                <LogsIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteNode(p.row.id, p.row.name)} sx={{ color: 'error.main' }}>
                                <DeleteIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    const clusterColumns = [
        {
            field: 'name', headerName: 'Cluster Name', flex: 1, minWidth: 180,
            renderCell: (p) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {p.row.isDefault && (
                        <Tooltip title="Default cluster — new organizations land here automatically">
                            <DefaultClusterIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                    )}
                    <Typography variant="body2">{p.row.name}</Typography>
                    {p.row.isDefault && (
                        <Chip label="Default" size="small" color="warning" variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                </Box>
            ),
        },
        {
            field: 'clusterType', headerName: 'Type', width: 130,
            renderCell: (p) => {
                const isTrinity = p.row.clusterType === 'O11Y_TRINITY';
                return <Chip label={isTrinity ? 'O11y Trinity' : 'ClickHouse'} size="small" variant="outlined" color={isTrinity ? 'info' : 'default'} />;
            }
        },
        {
            field: 'topology', headerName: 'Topology', width: 200,
            // Derive the human label from the actual {shards × replicas} pair
            // rather than the stored `topology` string. The old behaviour
            // collapsed every cluster to "Single-Shard Replicated" or
            // "Multi-Shard Replicated" regardless of the counts — so a
            // cluster created as "single_shard_replicated" with shardCount=2
            // rendered "Single-Shard Replicated · 2 shards · 1 replica/shard",
            // which is internally contradictory and exactly what staging
            // showed. Counts are the ground truth; the topology field can
            // drift but the math can't.
            renderCell: (p) => {
                const s = p.row.shardCount || 1;
                const r = p.row.replicasPerShard || 1;
                let label;
                if (s === 1 && r === 1)      label = 'Single Node';
                else if (s === 1 && r >= 2)  label = `Single-Shard Replicated · ${r}×`;
                else if (s >= 2 && r === 1)  label = `Sharded · ${s} shards`;
                else                          label = `Sharded + Replicated · ${s}×${r}`;
                return (
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {label}
                    </Typography>
                );
            },
        },
        { field: 'shardCount', headerName: 'Shards', width: 80 },
        { field: 'replicasPerShard', headerName: 'Replicas/Shard', width: 120 },
        {
            field: 'status', headerName: 'Status', width: 140,
            renderCell: (p) => (
                <Tooltip title={p.row.statusMessage || ''} placement="top" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label={p.value} size="small" color={clusterStatusColors[p.value] || 'default'} />
                        {p.value === 'CONFIGURING' && <CircularProgress size={14} />}
                    </Box>
                </Tooltip>
            ),
        },
        {
            field: 'actions', headerName: 'Actions', width: 380, sortable: false,
            renderHeader: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <span style={{ fontWeight: 600 }}>Actions</span>
                    <HelpIcon docKey="cluster_actions_legend" />
                </Box>
            ),
            renderCell: (p) => {
                const isLoading = clusterActionLoading[p.row.id];
                return (
                    <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                        {isLoading && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                        <Tooltip title="View Nodes">
                            <IconButton size="small" onClick={() => handleViewClusterNodes(p.row.id, p.row.name)}>
                                <InfoIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Assign Node">
                            <IconButton size="small" onClick={() => handleOpenAssignNode(p.row.id)}>
                                <AddIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Configure">
                            <IconButton size="small" onClick={() => handleConfigureCluster(p.row.id)} disabled={isLoading}>
                                <PreflightIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        {/* Migrate every MergeTree-family table in the
                            cluster's database to Replicated*MergeTree.
                            Only relevant on a real cluster (replicas≥2),
                            so we disable it on single-replica setups —
                            running it there is a no-op since the schema
                            initializer already emits the right engine on
                            fresh tables for single-node clusters. */}
                        <Tooltip title={p.row.replicasPerShard >= 2
                            ? 'Migrate existing MergeTree tables to Replicated*MergeTree (one-shot, idempotent)'
                            : 'Replication migration is only meaningful when replicas/shard ≥ 2'}>
                            <span>
                                <IconButton size="small"
                                    disabled={isLoading || (p.row.replicasPerShard || 1) < 2}
                                    onClick={() => askConfirm({
                                        title: `Migrate ${p.row.name} to Replicated engines?`,
                                        severity: 'warning',
                                        message:
                                            'For each non-Replicated table in the cluster\'s database, this performs a controlled ' +
                                            'RENAME → CREATE Replicated → INSERT SELECT → DROP backup. Brief write-window per table ' +
                                            '(~1 s during the RENAME) — the batch writer\'s retry queue absorbs the gap. ' +
                                            'Configure the cluster (Keeper + remote_servers + macros) FIRST; verify system.clusters ' +
                                            'returns rows and system.zookeeper is queryable. Idempotent: failed tables stay on the ' +
                                            'legacy MergeTree engine and a re-run picks them up.',
                                        confirmLabel: 'Migrate',
                                        confirmPhrase: p.row.name,
                                        onConfirm: () => handleMigrateToReplicated(p.row.id),
                                    })}
                                    sx={{ color: 'info.main' }}>
                                    <ReplicateIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {/* Set as the default landing zone for new orgs.
                            Only one cluster carries the flag at a time;
                            clicking on an already-default cluster is a no-op
                            (disabled). Disabled when the cluster has no
                            connection — orgs can't be auto-assigned to a
                            cluster the backend can't reach. */}
                        <Tooltip title={p.row.isDefault
                            ? 'This is the default cluster — new orgs land here'
                            : (p.row.hasConnection
                                ? 'Make this the default cluster for new organizations'
                                : 'Configure a JDBC connection first — default cluster needs reachable storage')}>
                            <span>
                                <IconButton size="small"
                                    disabled={isLoading || p.row.isDefault || !p.row.hasConnection}
                                    onClick={() => askConfirm({
                                        title: `Make ${p.row.name} the default cluster?`,
                                        severity: 'info',
                                        message:
                                            'Newly created organizations will be auto-assigned to this cluster. ' +
                                            'Existing tenants are unaffected — change their routing under Tenant Routing. ' +
                                            'Only one cluster can be the default; if another is currently flagged, the flag moves here.',
                                        confirmLabel: 'Set as default',
                                        onConfirm: () => handleSetDefaultCluster(p.row.id, p.row.name),
                                    })}
                                    sx={{ color: p.row.isDefault ? 'warning.main' : 'inherit' }}>
                                    {p.row.isDefault
                                        ? <DefaultClusterIcon sx={{ fontSize: 17 }} />
                                        : <NotDefaultClusterIcon sx={{ fontSize: 17 }} />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Deploy">
                            <IconButton size="small" onClick={() => { setClusterDeployId(p.row.id); setClusterDeployBinaryId(''); setClusterDeployOpen(true); }} disabled={isLoading}>
                                <DeployIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Start">
                            <IconButton size="small" onClick={() => handleStartCluster(p.row.id)} disabled={isLoading} sx={{ color: 'success.main' }}>
                                <PlayIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Graceful stop (drain first)">
                            <IconButton size="small" onClick={() => askConfirm({
                                title: `Graceful stop cluster ${p.row.name}?`,
                                severity: 'warning',
                                message: 'Drains in-flight merges on every DATA node (SYSTEM STOP MERGES, waits for system.merges to clear, SYSTEM FLUSH LOGS), then stops the cluster. Preferred over plain Stop for scheduled maintenance. If any node\'s drain times out, the stop is refused — you\'ll be re-prompted to force it.',
                                confirmLabel: 'Graceful stop',
                                confirmPhrase: p.row.name,
                                onConfirm: () => handleGracefulStopCluster(p.row.id, p.row.name),
                            })} disabled={isLoading} sx={{ color: 'info.main' }}>
                                <DrainIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Stop cluster (hard)">
                            <IconButton size="small" onClick={() => askConfirm({
                                title: `Hard stop cluster ${p.row.name}?`,
                                severity: 'warning',
                                message: 'Stops every ClickHouse node in this cluster. DATA nodes stop first, then KEEPER. ⚠ This is NOT a graceful drain — in-flight writes may be lost. Prefer Graceful Stop for scheduled maintenance. Tenants routed to this cluster will see datasource errors until it restarts.',
                                confirmLabel: 'Hard stop',
                                confirmPhrase: p.row.name,
                                onConfirm: () => handleStopCluster(p.row.id),
                            })} disabled={isLoading} sx={{ color: 'warning.main' }}>
                                <StopIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Health summary">
                            <IconButton size="small" onClick={() => handleClusterHealth(p.row.id, p.row.name)} disabled={isLoading}>
                                <HealthIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Health detail (disk / merges / slow queries)">
                            <IconButton size="small" onClick={() => setHealthDetail({ open: true, clusterId: p.row.id, clusterName: p.row.name })} disabled={isLoading}>
                                <HealthDetailIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete cluster">
                            <IconButton size="small" onClick={() => askConfirm({
                                title: `Delete cluster ${p.row.name}?`,
                                severity: 'error',
                                message: 'Removes the cluster entity and unassigns every node from it. Nodes return to the unassigned pool — underlying ClickHouse data is NOT touched. This is blocked if any tenant is still routed to this cluster.',
                                confirmLabel: 'Delete cluster',
                                confirmPhrase: p.row.name,
                                onConfirm: () => handleDeleteCluster(p.row.id),
                            })} sx={{ color: 'error.main' }}>
                                <DeleteIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    if (loading) {
        return <TableSkeleton rows={5} columns={4} />;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Storage Infrastructure</Typography>
                        <HelpIcon docKey="page_overview" size="medium" />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Manage ClickHouse instances, clusters, and replication. Upload binaries, deploy, and monitor nodes via SSH.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small"
                            onClick={() => setConsistencyOpen(true)}>
                        Consistency
                    </Button>
                    <Button variant="outlined" size="small"
                            onClick={() => { loadClusters(); setSqlConsole({ open: true, clusterId: null }); }}>
                        SQL console
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setAuditOpen(true)}
                            startIcon={<LogsIcon />}>
                        Audit log
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleStateExport}
                            disabled={exportRunning}>
                        {exportRunning ? 'Exporting…' : 'Export state'}
                    </Button>
                </Stack>
            </Box>

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>
            )}

            {/* Top-of-page operational health banner — aggregates cluster/node/WAL/retention/backup state */}
            <ClickHouseHealthBanner />

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab label={(<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>Nodes & Binaries <HelpIcon docKey="nodes_tab" /></Box>)} />
                    <Tab label={(<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>Clusters <HelpIcon docKey="clusters_tab" /></Box>)} />
                    <Tab label={(<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>Tenant Routing <HelpIcon docKey="tenant_routing_tab" /></Box>)} />
                    <Tab label={(<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>Backups <HelpIcon docKey="backups_tab" /></Box>)} />
                    <Tab label="Cardinality" />
                </Tabs>
            </Box>

            {/* ===== TAB 0: Nodes & Binaries ===== */}
            {activeTab === 0 && (<>

            {/* Binaries Section */}
            <Paper sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Binaries ({binaries.length})</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                    <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                        Select File
                        <input type="file" hidden onChange={(e) => setUploadFile(e.target.files[0])} />
                    </Button>
                    {uploadFile && <Typography variant="body2">{uploadFile.name}</Typography>}
                    <TextField label="Version" value={uploadVersion} onChange={(e) => setUploadVersion(e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                    <Button variant="contained" onClick={handleUploadBinary} disabled={!uploadFile || uploading}>
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                    <HelpIcon docKey="upload_binary" />
                </Box>
                {binaries.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {binaries.map(b => (
                            <Chip
                                key={b.id}
                                label={`${b.fileName} ${b.version ? `(v${b.version})` : ''} - ${formatSize(b.fileSize)}`}
                                onDelete={() => handleDeleteBinary(b.id)}
                                variant="outlined"
                            />
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Nodes Section */}
            <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Nodes ({nodes.length})</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<LogsIcon />} onClick={() => handleViewLogs(null, null)}>All Logs</Button>
                        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAddNode}>Add Node</Button>
                        <HelpIcon docKey="add_node" />
                    </Box>
                </Box>
                {nodes.some(n => n.status === 'DEPLOYING') && (
                    <Box sx={{ mb: 1 }}>
                        <LinearProgress color="warning" />
                        <Typography variant="caption" sx={{ color: 'warning.main' }}>Deployment in progress - auto-refreshing...</Typography>
                    </Box>
                )}
                {/* Filter bar — free-text search + status chip filter. Visible only when there's enough nodes to be worth filtering. */}
                {nodes.length > 3 && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            placeholder="Search host / name / version…"
                            value={nodeFilter.search}
                            onChange={(e) => setNodeFilter(f => ({ ...f, search: e.target.value }))}
                            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" /> }}
                            sx={{ minWidth: 260 }}
                        />
                        {['all', 'RUNNING', 'STOPPED', 'ERROR', 'DEPLOYING', 'PENDING'].map(s => (
                            <Chip
                                key={s}
                                label={`${s.toLowerCase()} (${s === 'all' ? nodes.length : nodes.filter(n => n.status === s).length})`}
                                color={s === 'all' ? 'default' : statusColors[s]}
                                variant={nodeFilter.status === s ? 'filled' : 'outlined'}
                                size="small"
                                onClick={() => setNodeFilter(f => ({ ...f, status: s }))}
                            />
                        ))}
                        {(nodeFilter.search || nodeFilter.status !== 'all') && (
                            <Typography variant="caption" color="text.secondary">
                                {filteredNodes.length} of {nodes.length} shown
                            </Typography>
                        )}
                    </Box>
                )}
                <DataGrid
                    rows={nodes.length > 3 ? filteredNodes : nodes} columns={nodeColumns} autoHeight pageSize={10}
                    disableRowSelectionOnClick getRowId={(row) => row.id}
                    rowHeight={52}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-cell': { borderColor: theme.palette.divider },
                        '& .MuiDataGrid-columnHeaders': { borderColor: theme.palette.divider, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                    }}
                />
            </Paper>

            {/* Who gets emailed when ClickHouse turns unhealthy (disk/node/WAL/backup) */}
            <ClickHouseAlertRecipients />

            </>)}

            {/* ===== TAB 1: Clusters ===== */}
            {activeTab === 1 && (
                <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Clusters ({clusters.length})</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadClusters}>Refresh</Button>
                            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateClusterOpen(true)}>Create Cluster</Button>
                            <HelpIcon docKey="create_cluster" />
                        </Box>
                    </Box>
                    {clustersLoading ? (
                        <TableSkeleton rows={5} columns={4} />
                    ) : (
                        <DataGrid
                            rows={clusters} columns={clusterColumns} autoHeight pageSize={10}
                            disableRowSelectionOnClick getRowId={(row) => row.id}
                            rowHeight={52}
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-cell': { borderColor: theme.palette.divider },
                                '& .MuiDataGrid-columnHeaders': { borderColor: theme.palette.divider, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                            }}
                        />
                    )}
                </Paper>
            )}

            {/* ===== TAB 2: Tenant Routing ===== */}
            {activeTab === 2 && (
                <Box>
                    {/* Cluster Connections */}
                    <Paper sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="h6">Cluster Connections</Typography>
                                <HelpIcon docKey="configure_cluster_connection" />
                            </Box>
                            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadRoutingData}>Refresh</Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Configure connection details for each cluster. Only clusters with a connection can have tenants assigned.
                        </Typography>
                        {routingLoading ? (
                            <TableSkeleton rows={5} columns={4} />
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Cluster</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Connection Node</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Database</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Tenants</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                Actions
                                                <HelpIcon docKey="tenant_routing_actions_legend" />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clusters.map(c => {
                                        const matchingAvail = availableClusters.find(ac => ac.id === c.id);
                                        // Match jdbcUrl to a node name for friendly display
                                        let connNodeDisplay = null;
                                        if (c.jdbcUrl) {
                                            const urlMatch = c.jdbcUrl.match(/jdbc:(?:ch|clickhouse):\/\/([^/]+)/);
                                            const hostPort = urlMatch ? urlMatch[1] : null;
                                            const matchedNode = hostPort ? nodes.find(n => `${n.host}:${n.clickhouseHttpPort}` === hostPort) : null;
                                            connNodeDisplay = matchedNode ? `${matchedNode.name} (${hostPort})` : hostPort || c.jdbcUrl;
                                        }
                                        return (
                                            <TableRow key={c.id}>
                                                <TableCell>{c.name}</TableCell>
                                                <TableCell><Chip label={c.status} size="small" color={clusterStatusColors[c.status] || 'default'} /></TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                        {connNodeDisplay || <em style={{ color: '#999' }}>Not configured</em>}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{c.databaseName || '-'}</TableCell>
                                                <TableCell>{matchingAvail?.tenantCount ?? '-'}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Button size="small" variant="outlined" onClick={() => handleOpenConnDialog(c)}>
                                                            {c.hasConnection ? 'Edit' : 'Configure'}
                                                        </Button>
                                                        {c.hasConnection && (
                                                            <Button size="small" variant="outlined" color="success"
                                                                    onClick={() => handleTestConnection(c.id)}>
                                                                Test
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {clusters.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                                    No clusters created yet. Create clusters in the "Clusters" tab first.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>

                    {/* Tenant Assignments */}
                    <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="h6">Tenant Assignments</Typography>
                                <HelpIcon docKey="assign_tenant" />
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button variant="outlined" size="small"
                                        onClick={() => setTenantMigrationOpen(true)}
                                        disabled={clusters.length < 2}>
                                    Migrate tenant data
                                </Button>
                                <Button variant="contained" size="small" startIcon={<AddIcon />}
                                        onClick={() => setTenantAssignDialogOpen(true)}
                                        disabled={availableClusters.length === 0}>
                                    Assign Tenant
                                </Button>
                            </Stack>
                        </Box>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Tenants must be assigned to a ClickHouse cluster to use the datasource. Unassigned tenants will see "Datasource is unavailable".
                        </Alert>
                        {assignments.length > 0 ? (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Tenant</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Cluster</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Cluster Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Assigned</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assignments.map(a => (
                                        <TableRow key={a.id}>
                                            <TableCell>{a.organizationName}</TableCell>
                                            <TableCell>
                                                <Chip label={a.organizationSlug} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                            </TableCell>
                                            <TableCell>{a.clusterName}</TableCell>
                                            <TableCell>
                                                <Chip label={a.clusterStatus} size="small"
                                                      color={clusterStatusColors[a.clusterStatus] || 'default'} />
                                            </TableCell>
                                            <TableCell>
                                                {toServerDate(a.createdAt)?.toLocaleDateString() ?? '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Button size="small" color="error" variant="outlined"
                                                        onClick={() => handleUnassignTenant(a.organizationId, a.organizationName)}>
                                                    Unassign
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                No tenants assigned to any cluster. Assign tenants to enable their datasource.
                            </Typography>
                        )}

                        {unassignedTenants.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Unassigned Tenants ({unassignedTenants.length}) — datasource unavailable
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {unassignedTenants.map(t => (
                                        <Chip key={t.organizationId} label={`${t.organizationName} (${t.organizationSlug})`}
                                              size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Paper>

                    {/* Per-tenant data usage on each cluster */}
                    <ClickHouseTenantUsagePanel
                        clusters={clusters}
                        organizations={[
                            ...assignments.map(a => ({
                                id: a.organizationId,
                                name: a.organizationName,
                                slug: a.organizationSlug,
                            })),
                            ...(unassignedTenants || []).map(t => ({
                                id: t.organizationId,
                                name: t.organizationName,
                                slug: t.organizationSlug,
                            })),
                        ]}
                    />

                    {/* Per-tenant retention policies (ALTER DELETE mutations) */}
                    <ClickHouseRetentionPanel
                        organizations={[
                            ...assignments.map(a => ({
                                id: a.organizationId,
                                name: a.organizationName,
                                slug: a.organizationSlug,
                            })),
                            ...(unassignedTenants || []).map(t => ({
                                id: t.organizationId,
                                name: t.organizationName,
                                slug: t.organizationSlug,
                            })),
                        ]}
                    />

                    {/* Row Level Security provisioning status + backfill */}
                    <ClickHouseRlsPanel />
                </Box>
            )}

            {/* ===== TAB 3: Backups ===== */}
            {activeTab === 3 && (
                <ClickHouseBackupsTab clusters={clusters} />
            )}

            {/* ===== TAB 4: Cardinality ===== */}
            {activeTab === 4 && (
                <ClickHouseCardinalityPanel />
            )}

            {/* Assign Tenant Dialog */}
            <Dialog open={tenantAssignDialogOpen} onClose={() => setTenantAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Assign Tenant to ClickHouse Cluster
                        <HelpIcon docKey="assign_tenant" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Select Tenant</InputLabel>
                            <Select value={tenantAssignOrgId} onChange={(e) => setTenantAssignOrgId(e.target.value)} label="Select Tenant">
                                {unassignedTenants.map(t => (
                                    <MenuItem key={t.organizationId} value={t.organizationId}>
                                        {t.organizationName} ({t.organizationSlug})
                                    </MenuItem>
                                ))}
                                {/* Also allow reassigning already-assigned tenants */}
                                {assignments.map(a => (
                                    <MenuItem key={a.organizationId} value={a.organizationId}>
                                        {a.organizationName} ({a.organizationSlug}) — currently: {a.clusterName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Select Cluster</InputLabel>
                            <Select value={tenantAssignClusterId} onChange={(e) => setTenantAssignClusterId(e.target.value)} label="Select Cluster">
                                {availableClusters.map(c => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.name} — {c.tenantCount} tenant(s)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Alert severity="warning">
                            Changing a tenant's cluster will route all their ClickHouse queries and writes to the new cluster immediately.
                            Existing data in the old cluster will NOT be migrated automatically.
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTenantAssignDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAssignTenant}
                            disabled={!tenantAssignOrgId || !tenantAssignClusterId}>Assign</Button>
                </DialogActions>
            </Dialog>

            {/* Cluster Connection Dialog */}
            <Dialog open={connDialogOpen} onClose={() => setConnDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Configure Connection: {connClusterName}
                        <HelpIcon docKey="configure_cluster_connection" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Connection Node</InputLabel>
                            <Select
                                value={connForm.selectedNodeHost}
                                onChange={(e) => setConnForm({ ...connForm, selectedNodeHost: e.target.value })}
                                label="Connection Node"
                            >
                                {nodes
                                    .filter(n => n.clusterId === connClusterId && ['RUNNING', 'STOPPED'].includes(n.status))
                                    .map(n => {
                                        const hostPort = `${n.host}:${n.clickhouseHttpPort}`;
                                        return (
                                            <MenuItem key={n.id} value={hostPort}>
                                                {n.name} ({hostPort})
                                            </MenuItem>
                                        );
                                    })}
                                {nodes.filter(n => n.clusterId === connClusterId && ['RUNNING', 'STOPPED'].includes(n.status)).length === 0 && (
                                    <MenuItem disabled>No deployed nodes in this cluster</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Username" value={connForm.dbUsername}
                                       onChange={(e) => setConnForm({ ...connForm, dbUsername: e.target.value })}
                                       fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="Password" type="password" value={connForm.dbPassword}
                                       onChange={(e) => setConnForm({ ...connForm, dbPassword: e.target.value })}
                                       fullWidth InputLabelProps={{ shrink: true }}
                                       helperText="Leave empty to keep current" />
                        </Box>
                        <TextField label="Database Name" value={connForm.databaseName}
                                   onChange={(e) => setConnForm({ ...connForm, databaseName: e.target.value })}
                                   fullWidth InputLabelProps={{ shrink: true }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConnDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveConnection}
                            disabled={!connForm.selectedNodeHost}>Save Connection</Button>
                </DialogActions>
            </Dialog>

            {/* Create Cluster Dialog */}
            <Dialog open={createClusterOpen} onClose={() => setCreateClusterOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Create Storage Cluster
                        <HelpIcon docKey="create_cluster" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Cluster Name" value={newCluster.name} onChange={(e) => setNewCluster({ ...newCluster, name: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} required />
                        <FormControl fullWidth>
                            <InputLabel>Cluster Type</InputLabel>
                            <Select value={newCluster.clusterType} onChange={(e) => setNewCluster({ ...newCluster, clusterType: e.target.value })} label="Cluster Type">
                                <MenuItem value="CLICKHOUSE">ClickHouse</MenuItem>
                                <MenuItem value="O11Y_TRINITY">O11y Trinity</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Topology</InputLabel>
                            <Select value={newCluster.topology} onChange={(e) => setNewCluster({ ...newCluster, topology: e.target.value })} label="Topology">
                                <MenuItem value="single_shard_replicated">Single-Shard Replicated</MenuItem>
                                <MenuItem value="multi_shard_replicated">Multi-Shard Replicated</MenuItem>
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Shard Count" type="number" value={newCluster.shardCount} onChange={(e) => setNewCluster({ ...newCluster, shardCount: Math.max(1, parseInt(e.target.value) || 1) })} fullWidth InputLabelProps={{ shrink: true }} inputProps={{ min: 1 }} />
                            <TextField label="Replicas per Shard" type="number" value={newCluster.replicasPerShard} onChange={(e) => setNewCluster({ ...newCluster, replicasPerShard: Math.max(1, parseInt(e.target.value) || 1) })} fullWidth InputLabelProps={{ shrink: true }} inputProps={{ min: 1 }} />
                        </Box>
                        <Alert severity="info">
                            A cluster requires at least 3 keeper nodes for quorum and enough data nodes to fill all shards and replicas.
                            Total data nodes needed: {newCluster.shardCount * newCluster.replicasPerShard}
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateClusterOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateCluster} disabled={!newCluster.name}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Assign Node Dialog */}
            <Dialog open={assignNodeOpen} onClose={() => setAssignNodeOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Assign Node to Cluster
                        <HelpIcon docKey="assign_node_to_cluster" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {unassignedNodes.length === 0 ? (
                            <Alert severity="warning">No unassigned nodes available. Add nodes in the "Nodes & Binaries" tab first.</Alert>
                        ) : (
                            <>
                                <FormControl fullWidth>
                                    <InputLabel>Select Node</InputLabel>
                                    <Select value={assignForm.nodeId} onChange={(e) => setAssignForm({ ...assignForm, nodeId: e.target.value })} label="Select Node">
                                        {unassignedNodes.map(n => (
                                            <MenuItem key={n.id} value={n.id}>{n.name} ({n.host})</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel>Node Role</InputLabel>
                                    <Select value={assignForm.nodeRole} onChange={(e) => setAssignForm({ ...assignForm, nodeRole: e.target.value })} label="Node Role">
                                        <MenuItem value="DATA">Data Node</MenuItem>
                                        <MenuItem value="KEEPER">Keeper Node</MenuItem>
                                        {/* Combined role for small clusters where the same physical
                                            host runs both the data shard and an embedded Keeper.
                                            Treated as a member of both sets server-side. */}
                                        <MenuItem value="DATA_AND_KEEPER">Data + Keeper (combined)</MenuItem>
                                    </Select>
                                </FormControl>
                                {(assignForm.nodeRole === 'DATA' || assignForm.nodeRole === 'DATA_AND_KEEPER') && (
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField label="Shard Number" type="number" value={assignForm.shardNum} onChange={(e) => setAssignForm({ ...assignForm, shardNum: parseInt(e.target.value) || 1 })} fullWidth InputLabelProps={{ shrink: true }} inputProps={{ min: 1 }} />
                                        <TextField label="Replica Name" value={assignForm.replicaName} onChange={(e) => setAssignForm({ ...assignForm, replicaName: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} placeholder="e.g., replica_1" />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignNodeOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAssignNode} disabled={!assignForm.nodeId || unassignedNodes.length === 0}>Assign</Button>
                </DialogActions>
            </Dialog>

            {/* Cluster Deploy Dialog */}
            <Dialog open={clusterDeployOpen} onClose={() => setClusterDeployOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Deploy Cluster
                        <HelpIcon docKey="deploy_cluster_binary" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This will deploy the selected binary to all nodes in the cluster. Keeper nodes are deployed first, then data nodes.
                        </Alert>
                        {binaries.length === 0 ? (
                            <Alert severity="warning">No binaries uploaded. Upload a ClickHouse binary in the "Nodes & Binaries" tab first.</Alert>
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel>Select Binary</InputLabel>
                                <Select value={clusterDeployBinaryId} onChange={(e) => setClusterDeployBinaryId(e.target.value)} label="Select Binary">
                                    {binaries.map(b => (
                                        <MenuItem key={b.id} value={b.id}>
                                            {b.fileName} {b.version ? `(v${b.version})` : ''} - {formatSize(b.fileSize)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClusterDeployOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleDeployCluster} disabled={!clusterDeployBinaryId || binaries.length === 0}>Deploy Cluster</Button>
                </DialogActions>
            </Dialog>

            {/* Cluster Health Dialog */}
            <Dialog open={clusterHealthOpen} onClose={() => setClusterHealthOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Cluster Health: {clusterHealthName}
                        <HelpIcon docKey="cluster_health" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {clusterHealthLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                            <TableSkeleton rows={5} columns={4} />
                            <Typography variant="body2" color="text.secondary">Checking cluster health...</Typography>
                        </Box>
                    ) : clusterHealthResult ? (
                        <Box sx={{ mt: 1 }}>
                            {clusterHealthResult.error && (
                                <Alert severity="error" sx={{ mb: 2 }}>{clusterHealthResult.error}</Alert>
                            )}
                            {clusterHealthResult.clusterStatus && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2 }}>
                                    <MetricCard label="Cluster Status" value={clusterHealthResult.clusterStatus} color={clusterHealthResult.clusterStatus === 'RUNNING' ? 'success.main' : 'warning.main'} />
                                    <MetricCard label="Total Nodes" value={clusterHealthResult.totalNodes || '-'} />
                                    <MetricCard label="Healthy" value={clusterHealthResult.healthy ?? '0'} color="success.main" />
                                    <MetricCard label="Unhealthy" value={clusterHealthResult.unhealthy ?? '0'} color={clusterHealthResult.unhealthy > 0 ? 'error.main' : 'text.primary'} />
                                </Box>
                            )}
                            {clusterHealthResult.nodes && (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Node</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Shard</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Replica</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clusterHealthResult.nodes.map((n, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{n.nodeName} <Typography component="span" variant="caption" color="text.secondary">({n.nodeHost})</Typography></TableCell>
                                                <TableCell><Chip label={n.role || 'DATA'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /></TableCell>
                                                <TableCell>{n.shard || '-'}</TableCell>
                                                <TableCell>{n.replica || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip label={n.success ? 'Healthy' : 'Unhealthy'} size="small"
                                                          color={n.success ? 'success' : 'error'} sx={{ fontSize: '0.7rem' }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClusterHealthOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Cluster Nodes Dialog */}
            <Dialog open={clusterNodesOpen} onClose={() => setClusterNodesOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Cluster Nodes: {clusterNodesName}
                        <HelpIcon docKey="view_cluster_nodes" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {clusterNodesLoading ? (
                        <TableSkeleton rows={5} columns={4} />
                    ) : clusterNodes.length === 0 ? (
                        <Alert severity="info" sx={{ mt: 1 }}>No nodes assigned to this cluster yet.</Alert>
                    ) : (
                        <>
                        <ClickHouseTopologyDiagram cluster={clusterNodesCluster} nodes={clusterNodes} />
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Host</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Shard</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Replica</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clusterNodes.map((n) => (
                                    <TableRow key={n.id}>
                                        <TableCell>{n.name}</TableCell>
                                        <TableCell>{n.host}</TableCell>
                                        <TableCell><Chip label={n.nodeRole || 'DATA'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /></TableCell>
                                        <TableCell>{n.shardNum || '-'}</TableCell>
                                        <TableCell>{n.replicaName || '-'}</TableCell>
                                        <TableCell><Chip label={n.status} size="small" color={statusColors[n.status] || 'default'} sx={{ fontSize: '0.7rem' }} /></TableCell>
                                        <TableCell>
                                            <Tooltip title="Remove from Cluster">
                                                <IconButton size="small" onClick={() => handleRemoveNodeFromCluster(assignClusterId || clusters.find(c => c.name === clusterNodesName)?.id, n.id)} sx={{ color: 'error.main' }}>
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClusterNodesOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Add Node Dialog */}
            <Dialog open={addOpen} onClose={handleCloseNodeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {editingNodeId ? 'Edit ClickHouse Node' : 'Add ClickHouse Node'}
                        <HelpIcon docKey="add_node" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Name" value={newNode.name} onChange={(e) => setNewNode({ ...newNode, name: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} required />
                        <TextField label="Host" value={newNode.host} onChange={(e) => setNewNode({ ...newNode, host: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} required helperText="IP address or hostname" />
                        <Divider><Typography variant="caption" color="text.secondary">SSH Configuration</Typography></Divider>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="SSH Port" type="number" value={newNode.sshPort} onChange={(e) => setNewNode({ ...newNode, sshPort: parseInt(e.target.value) || 22 })} sx={{ width: 120 }} InputLabelProps={{ shrink: true }} />
                            <TextField label="SSH Username" value={newNode.sshUsername} onChange={(e) => setNewNode({ ...newNode, sshUsername: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                        </Box>
                        <FormControl fullWidth>
                            <InputLabel>Auth Type</InputLabel>
                            <Select value={newNode.sshAuthType} onChange={(e) => setNewNode({ ...newNode, sshAuthType: e.target.value })} label="Auth Type">
                                <MenuItem value="PASSWORD">Password</MenuItem>
                                <MenuItem value="KEY">SSH Key</MenuItem>
                            </Select>
                        </FormControl>
                        {newNode.sshAuthType === 'PASSWORD' ? (
                            <TextField label="SSH Password" type="password" value={newNode.sshPassword} onChange={(e) => setNewNode({ ...newNode, sshPassword: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} helperText={editingNodeId ? 'Leave as ******** to keep the current password; type a new one to replace it' : undefined} />
                        ) : (
                            <TextField label="SSH Private Key" value={newNode.sshPrivateKey} onChange={(e) => setNewNode({ ...newNode, sshPrivateKey: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} multiline rows={4} helperText={editingNodeId ? 'Leave as ******** to keep the current key; paste a new private key to replace it' : 'Paste your private key here'} />
                        )}
                        <Divider><Typography variant="caption" color="text.secondary">ClickHouse Ports</Typography></Divider>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="HTTP Port" type="number" value={newNode.clickhouseHttpPort} onChange={(e) => setNewNode({ ...newNode, clickhouseHttpPort: parseInt(e.target.value) || 8123 })} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="TCP Port" type="number" value={newNode.clickhouseTcpPort} onChange={(e) => setNewNode({ ...newNode, clickhouseTcpPort: parseInt(e.target.value) || 9000 })} fullWidth InputLabelProps={{ shrink: true }} />
                        </Box>
                        <TextField label="Data Directory" value={newNode.dataDir} onChange={(e) => setNewNode({ ...newNode, dataDir: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNodeDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddNode} disabled={!newNode.name || !newNode.host}>{editingNodeId ? 'Save Changes' : 'Add Node'}</Button>
                </DialogActions>
            </Dialog>

            {/* Deploy Dialog */}
            <Dialog open={deployOpen} onClose={() => setDeployOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Deploy ClickHouse
                        <HelpIcon docKey="deploy_binary" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This will upload the selected binary to the remote node, install ClickHouse, configure it with production settings, and start the service.
                        </Alert>
                        {binaries.length === 0 ? (
                            <Alert severity="warning">No binaries uploaded. Upload a ClickHouse binary first.</Alert>
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel>Select Binary</InputLabel>
                                <Select value={selectedBinaryId} onChange={(e) => setSelectedBinaryId(e.target.value)} label="Select Binary">
                                    {binaries.map(b => (
                                        <MenuItem key={b.id} value={b.id}>
                                            {b.fileName} {b.version ? `(v${b.version})` : ''} - {formatSize(b.fileSize)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeployOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleDeploy} disabled={!selectedBinaryId || binaries.length === 0 || deployInFlight}>
                        {deployInFlight ? 'Deploying...' : 'Deploy'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pre-flight Dialog */}
            <Dialog open={preflightOpen} onClose={() => setPreflightOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Pre-flight Checks: {preflightNodeName}
                        <HelpIcon docKey="preflight_check" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {preflightLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                            <TableSkeleton rows={5} columns={4} />
                            <Typography variant="body2" color="text.secondary">Running pre-flight checks via SSH...</Typography>
                        </Box>
                    ) : preflightResult ? (
                        <Box sx={{ mt: 1 }}>
                            {preflightResult.error && !preflightResult.checks?.length && (
                                <Alert severity="error" sx={{ mb: 2 }}>{preflightResult.error}</Alert>
                            )}
                            {preflightResult.passed != null && (
                                <Alert severity={preflightResult.passed ? 'success' : 'error'} sx={{ mb: 2 }}>
                                    {preflightResult.passed
                                        ? `All checks passed${preflightResult.warnings > 0 ? ` (${preflightResult.warnings} warnings)` : ''}`
                                        : `${preflightResult.errors} error(s), ${preflightResult.warnings} warning(s)`}
                                </Alert>
                            )}
                            {preflightResult.checks?.length > 0 && (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Check</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Detail</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preflightResult.checks.map((c, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{c.name}</TableCell>
                                                <TableCell><PreflightStatusIcon status={c.status} /></TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                                        {c.detail}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreflightOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Deployment Logs Dialog — Terminal Viewer */}
            <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { height: '70vh', bgcolor: '#0d1117' } }}>
                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <DeploymentLogViewer nodeId={logsNodeId} nodeName={logsNodeName || 'All Nodes'} />
                </DialogContent>
                <DialogActions sx={{ bgcolor: '#0d1117', borderTop: '1px solid #333' }}>
                    <Button onClick={() => setLogsOpen(false)} sx={{ color: '#888' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Deep Health Dialog */}
            <Dialog open={deepHealthOpen} onClose={() => setDeepHealthOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Deep Health: {deepHealthNodeName}
                        <HelpIcon docKey="deep_health" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {deepHealthLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                            <TableSkeleton rows={5} columns={4} />
                            <Typography variant="body2" color="text.secondary">Querying ClickHouse system tables...</Typography>
                        </Box>
                    ) : deepHealthResult ? (
                        <Box sx={{ mt: 1 }}>
                            {deepHealthResult.error && (
                                <Alert severity="error" sx={{ mb: 2 }}>{deepHealthResult.error}</Alert>
                            )}
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
                                <MetricCard label="Status" value={deepHealthResult.success ? 'Healthy' : 'Unhealthy'} color={deepHealthResult.success ? 'success.main' : 'error.main'} />
                                <MetricCard label="Response Time" value={deepHealthResult.responseTimeMs ? `${deepHealthResult.responseTimeMs}ms` : '-'} />
                                <MetricCard label="Version" value={deepHealthResult.version || '-'} />
                                <MetricCard label="Uptime" value={deepHealthResult.uptime || '-'} />
                                <MetricCard label="Memory Usage" value={deepHealthResult.memoryUsage || '-'} />
                                <MetricCard label="Active Queries" value={deepHealthResult.activeQueries || '-'} />
                                <MetricCard label="Active Merges" value={deepHealthResult.activeMerges || '-'} />
                                {deepHealthResult.keeperStatus && (
                                    <MetricCard label="Keeper" value={deepHealthResult.keeperStatus.startsWith('connected') ? 'Connected' : 'Unreachable'} color={deepHealthResult.keeperStatus.startsWith('connected') ? 'success.main' : 'error.main'} />
                                )}
                            </Box>
                            <ClickHouseResultTable title="Disk Information" data={deepHealthResult.disks} />
                            <ClickHouseResultTable title="Database Sizes" data={deepHealthResult.databases} emptyText="No databases with data yet" />
                            <ClickHouseResultTable title="Tables with High Part Count (>50)" data={deepHealthResult.highPartsTables} titleColor="warning.main" emptyText="No tables with excessive parts" />
                            {/* Replication + detached-parts signals were collected by the backend but never rendered — the reason you'd open Deep Health on a replicated cluster. */}
                            <ClickHouseResultTable title="Replication Lag (>10s)" data={deepHealthResult.replicationLag} titleColor="warning.main" emptyText="No lagging replicas" />
                            <ClickHouseResultTable title="Replication Queue" data={deepHealthResult.replicationQueue} titleColor="warning.main" emptyText="Replication queue is empty" />
                            <ClickHouseResultTable title="Detached Parts" data={deepHealthResult.detachedParts} titleColor="warning.main" emptyText="No detached parts" />
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeepHealthOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Node Confirmation Dialog */}
            <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Delete Node
                        <HelpIcon docKey="delete_node" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>{deleteNodeName}</strong>? This will remove the node from VerOps and delete all associated deployment logs.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deleteCleanRemote}
                                onChange={(e) => setDeleteCleanRemote(e.target.checked)}
                                color="error"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Also remove ClickHouse from remote server
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Stops ClickHouse, removes binaries, config, data, logs, and the clickhouse user from the server
                                </Typography>
                            </Box>
                        }
                    />
                    {deleteCleanRemote && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This will permanently delete all ClickHouse data on the remote server. This action cannot be undone.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
                    >
                        {deleteLoading ? 'Deleting...' : deleteCleanRemote ? 'Delete & Clean Server' : 'Delete Node'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Shared typed-confirmation dialog for destructive actions */}
            <ConfirmDialog
                open={confirmState.open}
                title={confirmState.title}
                message={confirmState.message}
                severity={confirmState.severity}
                confirmPhrase={confirmState.confirmPhrase}
                confirmLabel={confirmState.confirmLabel}
                onConfirm={async () => { await confirmState.onConfirm?.(); }}
                onClose={() => setConfirmState({ ...confirmState, open: false })}
            />

            {/* Per-node disk-budget dialog (keep_free_space_bytes enforcement) */}
            <Dialog open={budgetDialog.open} onClose={() => setBudgetDialog({ open: false, node: null, gb: '' })}
                    maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Disk budget — {budgetDialog.node?.name || ''}
                        <HelpIcon docKey="disk_budget" />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Sets ClickHouse <code>keep_free_space_bytes</code> so the server refuses new INSERTs once
                        used space crosses the budget. Existing data stays readable; merges continue.
                        <br /><br />
                        Node total disk: <strong>{budgetDialog.node?.diskTotalGb ?? '?'} GB</strong>
                        {budgetDialog.node?.diskFreeGb != null && (
                          <> · Currently used: <strong>{(budgetDialog.node.diskTotalGb ?? 0) - budgetDialog.node.diskFreeGb} GB</strong></>
                        )}
                        {budgetDialog.node?.diskBudgetGb != null && (
                          <> · Current budget: <strong>{budgetDialog.node.diskBudgetGb} GB</strong></>
                        )}
                    </Alert>
                    <TextField
                        fullWidth
                        type="number"
                        label="Budget (GB)"
                        value={budgetDialog.gb}
                        onChange={(e) => setBudgetDialog(b => ({ ...b, gb: e.target.value }))}
                        helperText={`Must be > 0 and ≤ ${budgetDialog.node?.diskTotalGb ?? 'disk total'} GB. Will be clamped server-side.`}
                        inputProps={{ min: 1, max: budgetDialog.node?.diskTotalGb }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBudgetDialog({ open: false, node: null, gb: '' })}
                            disabled={budgetSaving}>Cancel</Button>
                    {budgetDialog.node?.diskBudgetGb != null && (
                        <Button color="warning" onClick={() => saveBudget(true)}
                                disabled={budgetSaving}>Clear budget</Button>
                    )}
                    <Button variant="contained" onClick={() => saveBudget(false)}
                            disabled={budgetSaving}>
                        {budgetSaving ? 'Applying…' : 'Apply'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cluster health detail: disk / merges / slow queries / mutations / replication */}
            <ClickHouseHealthDetailDialog
                open={healthDetail.open}
                clusterId={healthDetail.clusterId}
                clusterName={healthDetail.clusterName}
                onClose={() => setHealthDetail({ open: false, clusterId: null, clusterName: '' })}
            />

            {/* Operator audit log — merged OperatorAuditEvent + DeploymentLog + BackupJob feed */}
            <ClickHouseAuditLogDialog open={auditOpen} onClose={() => setAuditOpen(false)} />

            {/* Operator state consistency report */}
            <ClickHouseConsistencyDialog open={consistencyOpen} onClose={() => setConsistencyOpen(false)} />

            {/* Cross-cluster tenant data migration recipe */}
            <ClickHouseTenantMigrationDialog
                open={tenantMigrationOpen}
                onClose={() => setTenantMigrationOpen(false)}
                clusters={clusters}
                assignments={assignments}
                unassignedTenants={unassignedTenants}
            />

            {/* Read-only SQL console — SELECT/SHOW/DESCRIBE/EXPLAIN against the cluster leader */}
            <ClickHouseSqlConsoleDialog
                open={sqlConsole.open}
                clusters={clusters}
                defaultClusterId={sqlConsole.clusterId}
                onClose={() => setSqlConsole({ open: false, clusterId: null })}
            />
        </Box>
    );
};

const MetricCard = ({ label, value, color }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    return (
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>{label}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', color: color || 'text.primary' }}>{value}</Typography>
        </Paper>
    );
};

const COLUMN_LABELS = {
    database: 'Database', table: 'Table', size: 'Size', total_rows: 'Total Rows',
    parts: 'Parts', name: 'Name', path: 'Path', free: 'Free', total: 'Total', free_pct: 'Free %',
};

const ClickHouseResultTable = ({ title, data, titleColor, emptyText }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    let parsed = null;
    try {
        parsed = typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
        // not valid JSON, skip
    }

    if (!parsed) return null;

    // ClickHouse JSON format has { meta, data, rows }
    const meta = parsed.meta;
    const rows = parsed.data;
    const hasData = rows && rows.length > 0;

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: titleColor || 'text.primary' }}>
                {title}
            </Typography>
            {!hasData ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                    {emptyText || 'No data'}
                </Typography>
            ) : (
                <Table size="small" sx={{
                    bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                    borderRadius: 1,
                    '& .MuiTableCell-root': { fontSize: '0.8rem', py: 0.75, px: 1.5 },
                }}>
                    <TableHead>
                        <TableRow>
                            {meta.map((col) => (
                                <TableCell key={col.name} sx={{ fontWeight: 600 }}>
                                    {COLUMN_LABELS[col.name] || col.name}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, i) => (
                            <TableRow key={i}>
                                {meta.map((col) => (
                                    <TableCell key={col.name} sx={{
                                        fontFamily: col.type.includes('Int') || col.type.includes('Float') ? 'monospace' : 'inherit',
                                        textAlign: col.type.includes('Int') || col.type.includes('Float') ? 'right' : 'left',
                                    }}>
                                        {col.name === 'free_pct'
                                            ? `${row[col.name]}%`
                                            : String(row[col.name] ?? '-')}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

        </Box>
    );
};

export default ClickHouseNodesPage;
